import React, { useState } from 'react';
import { Eye, EyeOff, LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

interface FloatingLabelInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  id: string;
  label: string;
  error?: string;
  icon?: LucideIcon;
  type?: string;
}

export const FloatingLabelInput: React.FC<FloatingLabelInputProps> = ({
  id,
  label,
  error,
  icon: Icon,
  type = 'text',
  value,
  onChange,
  className,
  ...props
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const isPassword = type === 'password';
  const inputType = isPassword ? (showPassword ? 'text' : 'password') : type;

  const hasValue = value !== undefined && value !== null && String(value).length > 0;

  return (
    <div className="relative space-y-1 font-heading">
      <div className="relative flex items-center min-h-[58px] rounded-xl border-2 border-input bg-card transition-all duration-200 focus-within:ring-2 focus-within:ring-primary focus-within:border-primary shadow-2xs">
        {Icon && (
          <div className="absolute left-4 top-1/2 -translate-y-1/2 text-foreground-muted pointer-events-none z-10">
            <Icon className="h-5 w-5" />
          </div>
        )}

        <input
          id={id}
          type={inputType}
          value={value}
          onChange={onChange}
          placeholder=" "
          className={cn(
            'peer w-full rounded-xl bg-transparent px-4 pt-5 pb-1.5 text-base font-semibold text-foreground transition-all focus:outline-none',
            Icon && 'pl-12',
            isPassword && 'pr-12',
            error && 'border-rose-500 focus:ring-rose-500',
            className
          )}
          {...props}
        />

        <label
          htmlFor={id}
          className={cn(
            'absolute font-heading transition-all duration-200 pointer-events-none select-none',
            Icon ? 'left-12' : 'left-4',
            hasValue
              ? 'top-1.5 text-[11px] font-extrabold text-primary tracking-wide'
              : 'top-4 text-sm font-semibold text-foreground-muted peer-placeholder-shown:top-4 peer-placeholder-shown:text-sm peer-placeholder-shown:font-semibold peer-focus:top-1.5 peer-focus:text-[11px] peer-focus:font-extrabold peer-focus:text-primary peer-focus:tracking-wide'
          )}
        >
          {label}
        </label>

        {isPassword && (
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3.5 top-1/2 -translate-y-1/2 text-foreground-muted hover:text-foreground transition-colors p-1.5 rounded-lg hover:bg-muted"
            title={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? <Eye className="h-5 w-5" /> : <EyeOff className="h-5 w-5" />}
          </button>
        )}
      </div>

      {error && <p className="text-xs font-bold text-rose-500 pl-1">{error}</p>}
    </div>
  );
};
